import { FormGroup } from '@/components/element/Form/FormGroup';
import { InputPrice } from '@/components/element/Form/InputPrice';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import { SelectSingle } from '@/components/element/Form/SelectSingle';
import { useInvalidatePaymentTaskExpenseAccountItems } from '@/hooks/api/2350_payment_task_expense_account_items';
import { useCreatePaymentTaskExpenseAccountItem } from '@/hooks/api/2360_create_payment_task_expense_account_item';
import { ExpenseAccountItem, PaymentTaskExpenseAccountItem } from '@/hooks/api/models';
import { toastApiError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentPaymentTask } from '@/providers/PaymentTaskProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { SingleValue } from 'react-select';

type FormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  task_id: number;
  expense_account_item_id: number;
  amount: number;
};

export const PaymentTaskExpenseAccountItemCreateForm = ({
  expenseAccountItems,
  paymentTaskExpenseAccountItems,
}: {
  expenseAccountItems: ExpenseAccountItem[];
  paymentTaskExpenseAccountItems: PaymentTaskExpenseAccountItem[];
}) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task } = useCurrentPaymentTask();
  const { mutate, data, isPending, error } = useCreatePaymentTaskExpenseAccountItem();
  const { invalidate } = useInvalidatePaymentTaskExpenseAccountItems({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    task_id: task!.id,
  });
  const [options, setOptions] = useState<SelectOptionType[]>([]);
  const {
    control,
    setValue,
    getValues,
    reset,
    trigger,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      task_id: task!.id,
    },
  });

  useEffect(() => {
    if (expenseAccountItems)
      setOptions(expenseAccountItems.map((d): SelectOptionType => ({ value: d.id, label: d.label })));
  }, [expenseAccountItems]);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (data) {
      invalidate();
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  const onExpenseAccountItemChange = (sv: SingleValue<SelectOptionType>) => {
    if (sv) {
      setValue('expense_account_item_id', sv.value);
      trigger('expense_account_item_id');
    } else {
      reset(undefined, { keepDefaultValues: true });
    }
  };

  return (
    <>
      <div className="col-12 col-md-4">
        <div className="form-group">
          <label>
            費用科目<span className="text-danger">*</span>
          </label>
          <Controller
            control={control}
            name="expense_account_item_id"
            rules={{ required: true }}
            render={({ field: { ref, ...field } }) => (
              <SelectSingle
                options={options}
                selectedId={getValues('expense_account_item_id')}
                onChange={onExpenseAccountItemChange}
                error={errors.expense_account_item_id != null}
              />
            )}
          />
        </div>
      </div>
      <div className="col-12 col-md-5">
        <FormGroup label="金額" required={true}>
          <div className="input-group">
            <Controller
              render={({ field: { onChange, value, ...field } }) => (
                <InputPrice onChange={onChange} value={value} error={errors.amount} />
              )}
              control={control}
              name="amount"
              rules={{ required: true }}
            />
          </div>
        </FormGroup>
      </div>
      <div className="col-12 col-md-3">
        <div className="form-group">
          <label>&nbsp;</label>
          <button className="btn w-100 btn-icon bg-gradient-dark" onClick={() => handleSubmit(onSubmit)()}>
            <i className="fa fa-plus"></i>追加
          </button>
        </div>
      </div>
    </>
  );
};
